/**
 * Voice playback (plan_real_guide_gaps.md, workstream V).
 *
 * ONE persistent HTMLAudioElement for the whole app, unlocked by a user
 * gesture and re-`src`d per utterance — the only pattern that survives mobile
 * autoplay policies (a fresh `new Audio()` per turn does not on iOS). Plays
 * server-rendered WAV (media element, not Web Audio) so the iOS silent
 * switch treats it as media.
 *
 * Contract: at most one utterance at a time; speaking something new (or the
 * visitor pressing the mic — the interruption bridge) stops the current one.
 */

import { getApiBaseUrl } from '../config/runtime';
import { authStorage } from './authStorage';

// One sample of silence as a data-URI WAV: used to unlock the element on a
// user gesture (canonical snippet — 44.1kHz mono 16-bit, 2 data bytes).
const SILENT_WAV =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';

const listeners = new Set();

const state = {
  status: 'idle', // idle | loading | playing
  key: null, // which button/turn owns the current utterance
  unlocked: false,
};

let audioEl = null;
let requestSeq = 0;
// Autoplay-blocked utterance parked for the visitor's first gesture (fresh
// page loads have no unlock even when voice mode is on).
let pendingUtterance = null;
let gestureListenerAttached = false;

function attachGestureReplay() {
  if (gestureListenerAttached || typeof document === 'undefined') return;
  gestureListenerAttached = true;
  document.addEventListener(
    'pointerdown',
    () => {
      gestureListenerAttached = false;
      const pending = pendingUtterance;
      pendingUtterance = null;
      if (!pending) return;
      // The gesture is happening NOW — unlock inside it, then replay.
      const unlocked = VoicePlaybackService.unlock();
      if (unlocked && unlocked.then) {
        unlocked.finally(() => VoicePlaybackService.speak(pending));
      } else {
        VoicePlaybackService.speak(pending);
      }
    },
    { once: true, capture: true }
  );
}

function getAudio() {
  if (!audioEl && typeof window !== 'undefined') {
    audioEl = new Audio();
    audioEl.preload = 'auto';
    audioEl.onended = () => setState('idle', null);
    audioEl.onerror = () => {
      if (state.status !== 'idle') setState('idle', null);
    };
  }
  return audioEl;
}

function setState(status, key) {
  state.status = status;
  state.key = key;
  listeners.forEach((fn) => {
    try {
      fn({ status, key });
    } catch (_) {}
  });
}

function absoluteUrl(path) {
  if (!path) return path;
  if (/^https?:/i.test(path)) return path;
  const base = getApiBaseUrl().replace(/\/+$/, '').replace(/\/api$/, '');
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

const VoicePlaybackService = {
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  getState() {
    return { ...state };
  },

  /** Call from ANY user gesture (voice toggle tap, listen tap) — primes the
   *  element so later programmatic .play() calls are allowed. Returns a
   *  promise that settles when the element is safe to re-src. */
  unlock() {
    const audio = getAudio();
    if (!audio || state.unlocked) return Promise.resolve();
    try {
      audio.muted = true;
      audio.src = SILENT_WAV;
      const p = audio.play();
      if (p && p.then) {
        return p
          .then(() => {
            audio.pause();
            audio.muted = false;
            state.unlocked = true;
          })
          .catch(() => {
            audio.muted = false;
          });
      }
    } catch (_) {}
    return Promise.resolve();
  },

  /**
   * Render + play text. `key` identifies the utterance owner (message/card id)
   * so buttons can show per-key playing state. Stops whatever was playing.
   */
  async speak({ text, key, projectId, chatId, messageId, title }) {
    const audio = getAudio();
    if (!audio || !text || !String(text).trim()) return false;

    // Order matters: stop() bumps requestSeq to invalidate in-flight
    // prepares, so claim OUR seq after it — not before.
    this.stop();
    const seq = ++requestSeq;
    setState('loading', key);

    let url;
    try {
      const token = await authStorage.getToken();
      const resp = await fetch(`${getApiBaseUrl().replace(/\/+$/, '')}/tts/prepare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          text: String(text),
          project_id: projectId || null,
          chat_id: chatId || null,
          message_id: messageId || null,
        }),
      });
      if (!resp.ok) throw new Error(`prepare failed (${resp.status})`);
      const body = await resp.json();
      url = body?.data?.url;
      if (!url) throw new Error('no playback url');
    } catch (err) {
      console.warn('[Voice] TTS prepare failed:', err?.message || err);
      if (seq === requestSeq) setState('idle', null);
      return false;
    }

    // A newer speak/stop superseded us while preparing.
    if (seq !== requestSeq) return false;

    try {
      audio.src = absoluteUrl(url);
      // Lock-screen metadata where the platform offers it.
      try {
        if ('mediaSession' in navigator) {
          // eslint-disable-next-line no-undef
          navigator.mediaSession.metadata = new MediaMetadata({
            title: title || 'Okio guide',
            artist: 'Okio',
          });
        }
      } catch (_) {}
      await audio.play();
      if (seq === requestSeq) setState('playing', key);
      return true;
    } catch (err) {
      console.warn('[Voice] playback failed:', err?.message || err);
      // Autoplay policy said no (fresh page load, no gesture yet): park the
      // utterance and replay it on the visitor's first tap anywhere.
      if (err?.name === 'NotAllowedError') {
        pendingUtterance = { text, key, projectId, chatId, messageId, title };
        attachGestureReplay();
      }
      if (seq === requestSeq) setState('idle', null);
      return false;
    }
  },

  /** Interruption bridge: instant halt + queue clear (there is no queue —
   *  one utterance at a time by construction). */
  stop() {
    requestSeq += 1; // invalidates any in-flight prepare
    pendingUtterance = null; // a parked utterance dies with the stop too
    const audio = getAudio();
    if (audio) {
      try {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      } catch (_) {}
    }
    if (state.status !== 'idle') setState('idle', null);
  },

  isSpeakingKey(key) {
    return state.key === key && state.status !== 'idle';
  },
};

export default VoicePlaybackService;
